import React, { useMemo } from "react";
import { Alert, Divider, Grid, Group, Paper, Stack, Text } from "@mantine/core";
import {
  IconAlertTriangle,
  IconCalendar,
  IconCurrencyDollar,
  IconScissors,
  IconStar,
} from "@tabler/icons-react";
import { formatDateTime } from "../../utils/timeUtils";
import { Client } from "../../types/client";
import { ClientDetailStats } from "../../utils/clientDetailStats";
import {
  ClientInsights,
  computeClientInsights,
} from "../../utils/clientInsights";
import styles from "./ClientOverviewPanel.module.css";

/** Loyalty account data for the Points tile (loyalty FE1 §4.1). */
export interface ClientLoyaltyOverviewData {
  pointsBalance: number;
  /** [BE-pending: lifetime_points exposure] — line omitted when absent. */
  lifetimePoints?: number | null;
}

interface ClientOverviewPanelProps {
  client: Client;
  stats: ClientDetailStats;
  timezone: string;
  /** Present only when the location's loyalty program is enabled. */
  loyalty?: ClientLoyaltyOverviewData | null;
}

function StatCard(props: {
  icon: React.ReactNode;
  label: string;
  value: string;
  iconWrapClass: string;
  subValue?: string;
}) {
  return (
    <Paper className={styles.statCard} p="lg" radius="lg" shadow="sm">
      <Group gap="md" wrap="nowrap" align="flex-start">
        <div className={`${styles.statIconWrap} ${props.iconWrapClass}`}>
          {props.icon}
        </div>
        <Stack gap={4}>
          <Text size="sm" fw={500} c="dimmed">
            {props.label}
          </Text>
          <Text className={styles.statValue}>{props.value}</Text>
          {props.subValue && (
            <Text size="xs" c="dimmed">
              {props.subValue}
            </Text>
          )}
        </Stack>
      </Group>
    </Paper>
  );
}

function formatCurrency(value: number): string {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

function InsightsSection({ insights }: { insights: ClientInsights }) {
  const hasTopServices = insights.topBookedServices.length > 0;
  const hasProducts =
    insights.retailUnitsPurchased > 0 || insights.lastProductBought !== null;
  const showFrequentSeparately =
    insights.frequentProduct &&
    (!insights.lastProductBought ||
      insights.frequentProduct.name !== insights.lastProductBought.name);

  return (
    <Stack gap="md" mt="md">
      {(insights.isHighRiskNoShow || insights.isHighRiskCancellation) && (
        <Stack gap="xs">
          {insights.isHighRiskNoShow && (
            <Alert
              color="red"
              variant="light"
              icon={<IconAlertTriangle size={18} />}
              className={styles.riskAlert}
            >
              <Text fw={600} size="sm">
                High Risk for No-Show
              </Text>
              <Text size="xs" c="dimmed">
                {Math.round(insights.noShowRate * 100)}% of past appointments
                were no-shows.
              </Text>
            </Alert>
          )}
          {insights.isHighRiskCancellation && (
            <Alert
              color="orange"
              variant="light"
              icon={<IconAlertTriangle size={18} />}
              className={styles.riskAlert}
            >
              <Text fw={600} size="sm">
                High Risk for Cancellations
              </Text>
              <Text size="xs" c="dimmed">
                {Math.round(insights.cancellationRate * 100)}% of past
                appointments were cancelled.
              </Text>
            </Alert>
          )}
        </Stack>
      )}

      <section className={styles.insightSection}>
        <Text size="sm" fw={600} c="dark.7" mb="xs">
          Top Booked Services
        </Text>
        {hasTopServices ? (
          <Stack gap={6}>
            {insights.topBookedServices.map((svc, idx) => (
              <Group
                key={`${svc.serviceName}-${svc.staffName}`}
                gap="sm"
                wrap="nowrap"
                align="center"
              >
                <Text className={styles.rank}>{idx + 1}.</Text>
                <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                  <Text size="sm" fw={500}>
                    {svc.serviceName}
                  </Text>
                  <Text size="xs" c="dimmed">
                    with {svc.staffName} · {svc.count}{" "}
                    {svc.count === 1 ? "visit" : "visits"}
                  </Text>
                </Stack>
              </Group>
            ))}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            No booked services yet.
          </Text>
        )}
      </section>

      <Divider />

      <section className={styles.insightSection}>
        <Text size="sm" fw={600} c="dark.7" mb="xs">
          Retail Purchases
        </Text>
        {hasProducts ? (
          <Stack gap={6}>
            <Text size="sm">
              <Text span fw={500} component="span">
                {insights.retailUnitsPurchased}
              </Text>{" "}
              {insights.retailUnitsPurchased === 1 ? "unit" : "units"} ·{" "}
              <Text span fw={500} component="span">
                {formatCurrency(insights.retailTotalValue)}
              </Text>{" "}
              total
            </Text>
            {insights.lastProductBought && (
              <Text size="xs" c="dimmed">
                Last bought: {insights.lastProductBought.name}
              </Text>
            )}
            {showFrequentSeparately && insights.frequentProduct && (
              <Text size="xs" c="dimmed">
                Frequent purchase: {insights.frequentProduct.name}
              </Text>
            )}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            No retail purchases yet.
          </Text>
        )}
      </section>
    </Stack>
  );
}

export default function ClientOverviewPanel({
  client,
  stats,
  timezone,
  loyalty = null,
}: ClientOverviewPanelProps) {
  const spent = stats.totalSpent.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

  const visits = stats.totalVisits > 0 ? String(stats.totalVisits) : "—";

  const lastVisit =
    stats.lastVisitDisplay ??
    (client.last_appointment
      ? formatDateTime(client.last_appointment, timezone, "MMM D, YYYY")
      : "—");

  const insights = useMemo(
    () => computeClientInsights(client.transactions),
    [client.transactions],
  );

  const statSpan = loyalty ? 3 : 4;

  return (
    <Stack gap="xl">
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: statSpan }}>
          <StatCard
            icon={<IconScissors size={24} stroke={1.75} />}
            label="Total Visits"
            value={visits}
            iconWrapClass={styles.iconTeal}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: statSpan }}>
          <StatCard
            icon={<IconCalendar size={24} stroke={1.75} />}
            label="Last Visit"
            value={lastVisit}
            iconWrapClass={styles.iconBlue}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: statSpan }}>
          <StatCard
            icon={<IconCurrencyDollar size={24} stroke={1.75} />}
            label="Total Spent"
            value={spent}
            iconWrapClass={styles.iconLime}
          />
        </Grid.Col>
        {loyalty && (
          <Grid.Col span={{ base: 12, md: statSpan }}>
            <StatCard
              icon={<IconStar size={24} stroke={1.75} />}
              label="Points"
              value={loyalty.pointsBalance.toLocaleString("en-US")}
              iconWrapClass={styles.iconTeal}
              subValue={
                loyalty.lifetimePoints != null
                  ? `${loyalty.lifetimePoints.toLocaleString("en-US")} lifetime`
                  : undefined
              }
            />
          </Grid.Col>
        )}
      </Grid>

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper className={styles.sectionCard} p="lg" radius="lg" shadow="sm">
            <Text className={styles.sectionTitle}>Insights</Text>
            <InsightsSection insights={insights} />
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper className={styles.sectionCard} p="lg" radius="lg" shadow="sm">
            <Text className={styles.sectionTitle}>
              Preferences &amp; Status
            </Text>
            <Stack gap="lg" mt="md">
              <div>
                <Text size="sm" fw={500} c="dimmed" mb="sm">
                  Opt-In Status
                </Text>
                <Group gap="xs">
                  <span
                    className={
                      client.is_opted_in_reminder
                        ? styles.pillLime
                        : styles.pillMuted
                    }
                  >
                    Reminder: {client.is_opted_in_reminder ? "YES" : "NO"}
                  </span>
                  <span
                    className={
                      client.is_opted_in_marketing
                        ? styles.pillLime
                        : styles.pillMuted
                    }
                  >
                    Marketing: {client.is_opted_in_marketing ? "YES" : "NO"}
                  </span>
                </Group>
              </div>
              <div>
                <Text size="sm" fw={500} c="dimmed" mb="sm">
                  Communication Preferences
                </Text>
                <Text size="sm" c="dark.7">
                  No additional communication preferences on file. Opt-in status
                  above controls reminders and marketing.
                </Text>
              </div>
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
